import { type ReactNode, useId } from "react";
import { cx } from "../cx";
import styles from "./RadioGroup.module.css";

export interface RadioOption<V extends string = string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string = string> {
  options: RadioOption<V>[];
  value: V | null;
  onChange: (value: V) => void;
  name?: string;
  orientation?: "horizontal" | "vertical";
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
}

export function RadioGroup<V extends string = string>({
  options,
  value,
  onChange,
  name,
  orientation = "horizontal",
  label,
  disabled,
  className,
}: RadioGroupProps<V>) {
  const autoName = useId();
  const labelId = useId();
  return (
    <div
      role="radiogroup"
      aria-labelledby={label ? labelId : undefined}
      className={cx(styles.group, styles[orientation], className)}
    >
      {label && (
        <span id={labelId} className={styles.groupLabel}>
          {label}
        </span>
      )}
      {options.map((o) => (
        <label key={o.value} className={cx(styles.option, (disabled || o.disabled) && styles.disabled)}>
          <input
            type="radio"
            className={styles.input}
            name={name ?? autoName}
            value={o.value}
            checked={value === o.value}
            disabled={disabled || o.disabled}
            onChange={() => onChange(o.value)}
          />
          <span className={styles.circle} aria-hidden="true" />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}
